import { Zap } from 'lucide-react'
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { useKioskEnvTemp, useKioskPower, useKioskPowerSeries } from '@/lib/kiosk'
import { Gauge, Panel, Spark } from '@/components/kiosk/kioskUi'
import { C, fmt } from '@/components/kiosk/kioskTheme'

function tempC(n: number | null | undefined): string {
  return n == null ? '—' : `${fmt(n, 1)} °C`
}

// Peak reference for the gauge sweep — the cluster tops out ~1.4 kW under full
// GPU load; scale to 1500 W so idle (~280 W) reads as a small, honest arc.
const PEAK_W = 1500

function cur(sym: string): string {
  return sym === 'EUR' ? '€' : sym
}

export function PowerTile({ onTap }: { onTap: () => void }) {
  const { data: p } = useKioskPower()
  const { data: series } = useKioskPowerSeries('24h')
  const { data: env } = useKioskEnvTemp()
  const w = p?.power_w ?? null
  const online = p?.online ?? false
  const color = C.gaugeFill
  const rack = env?.sensors[0]
  return (
    <Panel title="Énergie" icon={<Zap className="h-3.5 w-3.5" />} accent={color} onTap={onTap}>
      <div className="flex h-full flex-col items-center justify-between">
        <Gauge value={online ? w : 0} max={PEAK_W} color={color} size={128} thickness={9}>
          <div className="kmono text-3xl font-bold leading-none" style={{ color: C.ink }}>{fmt(w)}</div>
          <div className="text-[11px] font-semibold" style={{ color: C.dim }}>WATTS</div>
        </Gauge>
        <div className="w-full">
          <div className="mb-0.5 text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>conso — 24 h</div>
          <Spark points={(series?.points ?? []).map((x) => x.power_w)} color={color} height={26} />
        </div>
        <div className="grid w-full grid-cols-2 gap-2">
          <div className="rounded-lg px-2 py-1.5" style={{ background: C.panelHi }}>
            <div className="kmono text-base font-bold" style={{ color: C.ink }}>{fmt(p?.today?.kwh, 1)}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>kWh auj.</div>
          </div>
          <div className="rounded-lg px-2 py-1.5" style={{ background: C.panelHi }}>
            <div className="kmono text-base font-bold" style={{ color: C.green }}>
              {fmt(p?.today?.cost, 2)} {cur(p?.currency ?? 'EUR')}
            </div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>coût auj.</div>
          </div>
        </div>
        {rack && (
          <div className="grid w-full grid-cols-2 gap-2">
            <div className="rounded-lg px-2 py-1.5" style={{ background: C.panelHi }}>
              <div className="kmono text-base font-bold" style={{ color: C.amber }}>{tempC(rack.inside?.temp_c)}</div>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>rack int.</div>
            </div>
            <div className="rounded-lg px-2 py-1.5" style={{ background: C.panelHi }}>
              <div className="kmono text-base font-bold" style={{ color: C.cyan }}>{tempC(rack.outside?.temp_c)}</div>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>rack ext.</div>
            </div>
          </div>
        )}
      </div>
    </Panel>
  )
}

// Must be a function, not a module constant: the palette object is mutated on
// theme change, so a value captured at import time would freeze the dark grey.
const ax = () => ({ fontSize: 11, fill: C.dim })

export function PowerDetail() {
  const { data: p } = useKioskPower()
  const { data: series } = useKioskPowerSeries('24h')
  const { data: env } = useKioskEnvTemp()
  const w = p?.power_w ?? null
  const color = C.gaugeFill
  const data = (series?.points ?? []).map((x) => ({ t: x.b, w: x.power_w }))
  const c = cur(p?.currency ?? 'EUR')
  const rack = env?.sensors[0]
  return (
    <div className="grid h-full grid-cols-[260px_1fr] gap-5">
      <div className="flex flex-col items-center justify-center gap-5">
        <Gauge value={p?.online ? w : 0} max={PEAK_W} color={color} size={190} thickness={13}>
          <div className="kmono text-5xl font-bold leading-none" style={{ color: C.ink }}>{fmt(w)}</div>
          <div className="text-xs font-semibold tracking-widest" style={{ color: C.dim }}>WATTS</div>
        </Gauge>
        <div className="grid w-full grid-cols-3 gap-2 text-center">
          {[
            { k: 'kWh auj.', v: fmt(p?.today?.kwh, 1), col: C.ink },
            { k: 'coût auj.', v: `${fmt(p?.today?.cost, 2)} ${c}`, col: C.green },
            // today.kwh * 30 read as a fraction of reality for most of the day
            // (at 02:00 it projects from two hours of use). The backend already
            // scales by the elapsed fraction of the month — use that.
            { k: 'proj. mois', v: fmt(p?.projection?.month_kwh ?? null, 0), col: C.ink },
          ].map((s) => (
            <div key={s.k} className="rounded-lg px-2 py-2" style={{ background: C.panelHi }}>
              <div className="kmono text-lg font-bold" style={{ color: s.col }}>{s.v}</div>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>{s.k}</div>
            </div>
          ))}
        </div>
        {rack && (
          <div className="grid w-full grid-cols-2 gap-2 text-center">
            {[
              { k: 'rack int.', v: tempC(rack.inside?.temp_c), col: C.amber },
              { k: 'rack ext.', v: tempC(rack.outside?.temp_c), col: C.cyan },
            ].map((s) => (
              <div key={s.k} className="rounded-lg px-2 py-2" style={{ background: C.panelHi }}>
                <div className="kmono text-lg font-bold" style={{ color: s.col }}>{s.v}</div>
                <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>{s.k}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex min-h-0 flex-col">
        <div className="mb-1 text-xs uppercase tracking-widest" style={{ color: C.dim }}>Puissance — 24 h</div>
        <div className="min-h-0 flex-1">
          <ResponsiveContainer>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pwrfill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.line} vertical={false} />
              <XAxis dataKey="t" tick={ax()} stroke={C.line}
                tickFormatter={(v) => (typeof v === 'string' ? `${new Date(v).getHours()}h` : '')} minTickGap={40} />
              <YAxis tick={ax()} stroke={C.line} width={44} tickFormatter={(v) => `${v}W`} />
              <Tooltip
                contentStyle={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 12, color: C.ink }}
                labelFormatter={(v) => (typeof v === 'string' ? new Date(v).toLocaleString('fr-FR') : '')}
                formatter={(v) => [`${Math.round(Number(v))} W`, 'puissance']}
              />
              <Area type="monotone" dataKey="w" stroke={color} strokeWidth={2} fill="url(#pwrfill)" isAnimationActive={false} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
